import { useCallback } from 'react';
import uuid from 'react-uuid';
import { ApiService, CapTableFundOwnership, CapTablesService, FundOwnershipDetailWithChildren, ProFormaCapTableModel } from 'api';
import { ISSUE_PRICE_ALIAS } from 'common/constants/cap-table';
import { PREFERRED_STOCK } from 'common/constants/securityTypes';
import { NewRoundModelerData } from 'pages/CapTable/cap-table/components/proforma-captable-dialog/NewRoundModeler.props';
import { buildPreFxCellAnnotations } from 'pages/CapTable/cap-table/components/proforma-captable-dialog/NewRoundModeler.utils';
import { COST_BASIS_ALIAS } from 'pages/CapTable/fund-ownership/components/shares-ledger/constants';
import { CaptableColumn } from '../CaptableTable.props';

const USD_CURRENCY = 'USD';

interface UseAfterProformaCreateParams {
  nrmState: NewRoundModelerData | null | undefined;
  companyInfo: { id?: number; captable_currency?: string | null } | null | undefined;
}

// CapTableFundOwnership (generated from the read serializer) omits fund_ownership_detail,
// which the write endpoint accepts. Extend it locally instead of typing the payload as `any`.
// The write endpoint (backend FundOwnershipDetailSerializer) also has a stricter contract than
// the read-shaped FundOwnershipDetailWithChildren: `id` (0 for new, matching the id==0 "new
// record" convention used across cap-table save payloads) is required, and `acquisitions` must
// be omitted entirely rather than sent as `[]` (allow_empty=False on that field).
type FundOwnershipDetailWritePayload = Omit<FundOwnershipDetailWithChildren, 'id' | 'acquisitions'> & {
  id: number;
  acquisitions?: Array<FundOwnershipDetailWithChildren['acquisitions'][number]>;
};

type FundOwnershipUpdatePayload = CapTableFundOwnership & {
  fund_ownership_detail: Array<FundOwnershipDetailWritePayload>;
};

// Existing fund-ownership-detail rows copied from the primary cap table always carry an
// `acquisitions` array (possibly empty) per the read contract; re-sending an empty one on this
// endpoint fails validation, so strip the key rather than send `[]`.
const sanitizeFodForWrite = (fod: FundOwnershipDetailWithChildren): FundOwnershipDetailWritePayload => {
  if (fod.acquisitions.length > 0) return fod as FundOwnershipDetailWritePayload;
  const { acquisitions, ...rest } = fod;
  return rest as FundOwnershipDetailWritePayload;
};

const useAfterProformaCreate = ({ nrmState, companyInfo }: UseAfterProformaCreateParams) => {
  const isActive = !!(nrmState?.isEnabled && nrmState?.isValid && nrmState?.computed);

  const buildColumnsForProforma = useCallback(
    (activeColumns: CaptableColumn[]): CaptableColumn[] => {
      if (!isActive || !nrmState?.computed) return activeColumns;

      const preferredSecurities = activeColumns.filter(
        c => c.security_type === PREFERRED_STOCK && !c.is_deleted
      );
      const sortedByDate = [...preferredSecurities]
        .filter(c => c.investment_date?.trim())
        .sort((a, b) => new Date(b.investment_date!).getTime() - new Date(a.investment_date!).getTime());
      const sourcePreferred = sortedByDate[0] ?? preferredSecurities[0];
      if (!sourcePreferred) return activeColumns;

      // NRM always models in USD (per Insight's request). When the cap table's
      // own currency differs, plant a PRE_FX_CURRENCY annotation on issue_price
      // instead of writing the raw USD number as if it were captable_currency -
      // same mechanism the manual "Original Issue Price" chip already uses
      // (conditions.tsx), just pre-filled instead of requiring re-entry.
      const captableCurrency = companyInfo?.captable_currency;
      const needsFx = !!captableCurrency && captableCurrency !== USD_CURRENCY;

      const newSecurity: CaptableColumn = {
        ...sourcePreferred,
        id: 0,
        columnRef: uuid(),
        security_ref: null,
        underlying_security_ref: null,
        name: nrmState.securityName,
        shares_outstanding: String(Math.round(nrmState.computed.newRoundSharesIssued)),
        issue_price: nrmState.computed.newRoundIssuePrice.toFixed(15),
        investment_date: nrmState.investmentDate || null,
        seniority: (sourcePreferred.seniority ?? 0) + 1,
        ...(needsFx && {
          cell_annotations: buildPreFxCellAnnotations(ISSUE_PRICE_ALIAS, nrmState.computed.newRoundIssuePrice),
        }),
      };
      return [...activeColumns, newSecurity];
    },
    [isActive, nrmState, companyInfo]
  );

  const afterCreate = useCallback(
    async (newCap: ProFormaCapTableModel) => {
      if (!nrmState?.computed) return;
      const newCapId = newCap.id;
      const { computed, securityName, fundId, fundInvestment, investmentDate } = nrmState;
      const capturedCompanyId = companyInfo?.id;

      let copiedFods: FundOwnershipDetailWithChildren[] = [];
      let fundOwnershipId: number | undefined;
      try {
        const copyResult = await ApiService.apiCapTablesFundOwnershipCopyFromPrimaryCreate(newCapId);
        copiedFods = copyResult.fund_ownership_detail ?? [];
        fundOwnershipId = copyResult.id;
      } catch {
        try {
          const fo = await ApiService.apiCapTablesFundOwnershipRead(newCapId);
          fundOwnershipId = fo.id;
        } catch (err) {
          console.error('Failed to read fund ownership for proforma:', err);
        }
      }

      if (!fundInvestment || fundInvestment <= 0) {
        if (copiedFods.length > 0) {
          const payload: FundOwnershipUpdatePayload = {
            cap_table: newCapId,
            fund_ownership_detail: copiedFods.map(sanitizeFodForWrite),
          };
          await ApiService.apiCapTablesFundOwnershipUpdate(newCapId, payload);
        }
        return;
      }

      let newSecurityId: number | undefined;
      try {
        const proformaData = await CapTablesService.apiCapTablesRead(newCapId);
        const securities = proformaData?.securities ?? [];
        newSecurityId = securities.find(s => s.name === securityName)?.id ?? undefined;
        if (!fundOwnershipId && proformaData?.fund_ownership?.id) {
          fundOwnershipId = proformaData.fund_ownership.id;
        }
      } catch (err) {
        console.error('Failed to fetch proforma cap table after create:', err);
      }

      // Both ids are guaranteed by the API contract at this point (a freshly created proforma
      // always has a fund ownership record, and companyInfo is always present while this dialog
      // is open) — bail instead of sending a payload with missing required fields.
      if (newSecurityId == null || fundOwnershipId == null || capturedCompanyId == null) return;

      // Back this investment with a SharesAcquisition row, like every other fund investment,
      // instead of setting invested_capital directly — the backend recomputes it from the
      // acquisition (sync_fod_invested_capital_from_acquisitions). invested_capital below is
      // just a placeholder the backend overwrites.
      const captableCurrency = companyInfo?.captable_currency;
      const needsFx = !!captableCurrency && captableCurrency !== USD_CURRENCY;

      const newAcquisition = {
        purchase_date: investmentDate,
        shares: String(Math.round(computed.numberOfShares)),
        cost_basis: String(fundInvestment),
        ...(needsFx && { cell_annotations: buildPreFxCellAnnotations(COST_BASIS_ALIAS, fundInvestment) }),
      };

      // id: 0 marks this as a new row (same convention as securities).
      const newFod: FundOwnershipDetailWritePayload = {
        id: 0,
        is_deleted: false,
        fund_ownership: fundOwnershipId,
        security: newSecurityId,
        fund: fundId,
        shares: String(Math.round(computed.numberOfShares)),
        invested_capital: String(fundInvestment),
        investment_date: investmentDate || undefined,
        entity_reference: capturedCompanyId,
        order: copiedFods.length,
        custom_security_name: '',
        loan_value: '0',
        acquisitions: [newAcquisition],
      };

      const payload: FundOwnershipUpdatePayload = {
        id: fundOwnershipId,
        cap_table: newCapId,
        fund_ownership_detail: [...copiedFods.map(sanitizeFodForWrite), newFod],
      };
      await ApiService.apiCapTablesFundOwnershipUpdate(newCapId, payload);
    },
    [nrmState, companyInfo]
  );

  return {
    buildColumnsForProforma,
    afterCreate: isActive ? afterCreate : undefined,
  };
};

export default useAfterProformaCreate;
